import * as React from 'react';
import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import { IDisplayValue } from '../../../../_common/platform/ts/Interfaces';
import TreeSelect from '../../../../_common/platform/tsx/TreeSelect';
import { IEntityValuesTree } from '../../ts/stores/EntityModelsStore';
import { ngettext } from '../../../../_common/platform/ts/ngettext';
import { ITreeNode, ITreeNodesCollection } from '../../ts/Interfaces';

export interface IUserGroupsImportOutput {
    [group: string]: string[];
}

export interface IUserImportInput {
    key: string;
    displayName: string;
}

export interface IUserGroupImportInput {
    name: string;
    users: IUserImportInput[];
}

export interface IUserGroupsImportProps {
    value: IUserGroupsImportOutput;
    input: IUserGroupImportInput[];
    glob: IGlob;
    theme: IValuesCollection;

    onChange(value: IUserGroupsImportOutput): void;
}

interface IGroupHash {
    [name: string]: number;
}

const GROUP_TYPE = 'group-';
const USER_TYPE = 'user-';

function convertValueToTreeValues(value: IUserGroupsImportOutput, groupHash: IGroupHash) {
    const result: IDisplayValue[] = [];
    const groups = Object.keys(value);

    groups.forEach((group) => {
        result.push({ value: GROUP_TYPE + groupHash[group] });
        value[group].forEach((userKey) => {
            result.push({ value: USER_TYPE + groupHash[group] + '-' + userKey });
        });
    });

    return result;
}

function findGroupByID(value: number, groupHash: IGroupHash): string | undefined {
    return Object.keys(groupHash).find((key) => groupHash[key] === value);
}

export const UserGroupsImport: React.FC<IUserGroupsImportProps> = (props) => {
    const [value, setValue] = React.useState<IUserGroupsImportOutput>({});
    const [groupHash, setGroupHash] = React.useState<IGroupHash>({});

    React.useEffect(() => {
        const hash: IGroupHash = {};

        props.input.forEach((value, groupIndex) => {
            hash[value.name] = groupIndex;
        });
        setGroupHash(hash);

        const newValue: IUserGroupsImportOutput = {};

        if (props.value != null) {
            _.each(props.value, (obj, key) => {
                if (key === 'keyList') {
                    if (Array.isArray(obj)) {
                        obj.forEach((item) => {
                            const users = item['users'];

                            if (Array.isArray(users)) {
                                newValue[item['name']] = users.map((user) => user.key);
                            }
                        });
                    }
                }
            });
        }

        setValue(newValue);
    }, []);

    const handleOnValueChanged = (values: IDisplayValue[]) => {
        const newValue: IUserGroupsImportOutput = {};

        values.forEach((val) => {
            if (val.value.startsWith(GROUP_TYPE)) {
                const groupId = parseInt(val.value.replace(GROUP_TYPE, ''));
                const groupName = findGroupByID(groupId, groupHash);

                if (newValue[groupName] == null) {
                    newValue[groupName] = [];
                }
            }

            if (val.value.startsWith(USER_TYPE)) {
                const match = val.value.match(/user-(\d+)-(.+)/);

                if (match) {
                    const groupId = parseInt(match[1]);
                    const userId = match[2];
                    const groupName = findGroupByID(groupId, groupHash);

                    if (newValue[groupName] != null) {
                        const members = newValue[groupName];

                        members.push(userId);
                        newValue[groupName] = [...new Set(members)]; // unique elements only
                    } else {
                        newValue[groupName] = [userId];
                    }
                }
            }
        });

        setValue(newValue);

        if (typeof props.onChange === 'function') {
            props.onChange(newValue);
        }
    };

    const getInputGroups = () => {
        return props.input.map<ITreeNode>((group: IUserGroupImportInput) => {
            const id = GROUP_TYPE + groupHash[group.name];

            return {
                key: id,
                id: id,
                title: group.name,
                subNodes: group.users.map<ITreeNode>((user: IUserImportInput) => {
                    const id = USER_TYPE + groupHash[group.name] + '-' + user.key;

                    return {
                        key: id,
                        id: id,
                        title: user.displayName,
                        parentId: GROUP_TYPE + groupHash[group.name],
                        subNodes: []
                    };
                })
            };
        });
    };

    const getInputValues = () => {
        const result: ITreeNodesCollection = {};

        props.input.forEach((group: IUserGroupImportInput, index) => {
            const id = GROUP_TYPE + groupHash[group.name];
            const value: ITreeNode = {
                key: id + '_' + index,
                id: id,
                title: group.name,
                parentId: null,
                subNodes: getInputUserValues(groupHash[group.name], group.users, result)
            };

            result[id] = value;
        });

        return result;
    };

    const getInputUserGroupTree = () => {
        return () => {
            const def = $.Deferred<IEntityValuesTree>();
            const tree = getInputGroups();
            const values = getInputValues();
            const result: IEntityValuesTree = {
                tree: tree,
                nodes: values
            };

            def.resolve(result);
            return def;
        };
    };

    const getInputUserValues = (groupId: number, users: IUserImportInput[], result: ITreeNodesCollection) => {
        return users.map((user) => {
            const id = USER_TYPE + groupId + '-' + user.key;
            const value: ITreeNode = {
                key: id,
                id: id,
                title: user.displayName,
                parentId: GROUP_TYPE + groupId,
                subNodes: []
            };

            result[id] = value;
            return value;
        });
    };

    const getSelectedMessage = (value: IUserGroupsImportOutput) => {
        const groups = Object.keys(value);
        const usersCount = groups.reduce((acc, groupId) => acc + value[groupId].length, 0);

        if (groups.length === 0 && usersCount === 0) {
            return [<span key={'ask-ws-span'}>Select groups and users to import.</span>];
        }

        const arr = _.compact([
            groups.length > 0 ? (
                <div key={'dirs-div-ws'} className="selected-message-block">
                    <strong>{groups.length}</strong>
                    <span>{ngettext('group', 'groups', groups.length)}</span>
                </div>
            ) : null,

            usersCount > 0 ? (
                <div key={'template-div-ws'} className="selected-message-block">
                    <strong>{usersCount}</strong>
                    <span>{ngettext('user', 'users', usersCount)}</span>
                </div>
            ) : null
        ]);

        if (arr.length === 2) {
            arr.splice(1, 0, <span key={'and-span-ws'}>{'and'}</span>);
        }

        arr.unshift(<span key={'you-span-ws'}>{'You have selected'}</span>);

        return arr;
    };

    const dataSourceID = props.glob.EntityModelsStore.getDataSourceIDByName('Default');

    return (
        <div className="usergroup-import">
            <div className="selected-message">{getSelectedMessage(value)}</div>
            <TreeSelect
                value={convertValueToTreeValues(value, groupHash)}
                allowMultiselection={true}
                attributeData={{
                    attribute: 'wwUserGroupsTree'
                }}
                autoSelectChildNodes={true}
                dataSourceID={dataSourceID}
                theme={props.theme}
                glob={props.glob}
                getValues={getInputUserGroupTree()}
                onChange={handleOnValueChanged}
            />
        </div>
    );
};
